import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { axios } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiSearch,
  FiRefreshCw,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiUser,
  FiPhone,
  FiX,
  FiSend,
  FiCheck,
  FiAlertTriangle,
  FiExternalLink
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';

const TYPE_LABELS = {
  yoklama: { label: 'Yoklama Bildirimi', bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' },
  deneme: { label: 'Deneme Sonucu', bg: '#F5F3FF', text: '#7C3AED', border: '#DDD6FE' },
  turkiye_geneli_deneme: { label: 'TG Deneme Sonucu', bg: '#EEF2FF', text: '#4F46E5', border: '#C7D2FE' },
  muhasebe: { label: 'Ödeme Hatırlatma', bg: '#FEF3C7', text: '#D97706', border: '#FDE68A' },
  odeme: { label: 'Ödeme Hatırlatma', bg: '#FEF3C7', text: '#D97706', border: '#FDE68A' },
  rehberlik: { label: 'Rehberlik Bildirimi', bg: '#ECFDF5', text: '#059669', border: '#A7F3D0' },
  etut: { label: 'Etüt Bildirimi', bg: '#FDF2F8', text: '#DB2777', border: '#FBCFE8' },
  hesap_bilgileri: { label: 'Giriş Bilgileri', bg: '#F0FDFA', text: '#0D9488', border: '#99F6E4' },
  odev: { label: 'Ödev Bildirimi', bg: '#FFF7ED', text: '#EA580C', border: '#FFEDD5' },
  calisma_programi: { label: 'Çalışma Programı', bg: '#F1F5F9', text: '#475569', border: '#CBD5E1' },
};

function formatPhoneNumber(phone) {
  if (!phone) return '-';
  const clean = String(phone).replace(/[^\d]/g, '');
  if (clean.length === 12 && clean.startsWith('90')) {
    return `+90 (${clean.slice(2, 5)}) ${clean.slice(5, 8)} ${clean.slice(8, 10)} ${clean.slice(10, 12)}`;
  }
  if (clean.length === 10) {
    return `+90 (${clean.slice(0, 3)}) ${clean.slice(3, 6)} ${clean.slice(6, 8)} ${clean.slice(8, 10)}`;
  }
  return phone;
}

function formatTime(isoString) {
  if (!isoString) return '--:--';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '--:--';
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

function parseProviderResponse(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function GonderilenMesajlarPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({
    stats: {
      total: 0,
      sent: 0,
      failed: 0,
      pending: 0,
      success_rate: 0,
      today_total: 0,
      today_sent: 0,
      today_failed: 0,
      today_pending: 0,
    },
    filtered_stats: { total: 0, sent: 0, failed: 0, pending: 0 },
    grouped_by_date: [],
    logs: [],
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // UI state
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (startDate) params.baslangic_tarihi = startDate;
      if (endDate) params.bitis_tarihi = endDate;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const res = await axios.get('/bildirimler/gonderilen-mesajlar', { params });
      if (res.data?.success) {
        setData(res.data);
        // Expand all days by default on initial or filter load
        const allDates = new Set(res.data.grouped_by_date.map((g) => g.date));
        setExpandedDays(allDates);
      }
    } catch (err) {
      console.error('Mesaj logları yüklenemedi:', err);
      toast.error('Mesaj kayıtları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, typeFilter, startDate, endDate, searchQuery]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Handle Preset Date selection
  const handleDatePreset = (preset) => {
    setDatePreset(preset);
    const today = new Date();
    const toYMD = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    if (preset === 'today') {
      const t = toYMD(today);
      setStartDate(t);
      setEndDate(t);
    } else if (preset === 'yesterday') {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      const yStr = toYMD(y);
      setStartDate(yStr);
      setEndDate(yStr);
    } else if (preset === 'week') {
      const w = new Date();
      w.setDate(w.getDate() - 7);
      setStartDate(toYMD(w));
      setEndDate(toYMD(today));
    } else if (preset === 'month') {
      const m = new Date(today.getFullYear(), today.getMonth(), 1);
      setStartDate(toYMD(m));
      setEndDate(toYMD(today));
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  const toggleDay = (dateKey) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  const expandAll = () => {
    const all = new Set(data.grouped_by_date.map((g) => g.date));
    setExpandedDays(all);
  };

  const collapseAll = () => {
    setExpandedDays(new Set());
  };

  const stats = data.stats || {};
  const filteredStats = data.filtered_stats || {};

  return (
    <div className="gonderilen-mesajlar-page" style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 60, width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <style>{`
        .gonderilen-mesajlar-page,
        .gonderilen-mesajlar-page * {
          box-sizing: border-box;
        }
        .page-content:has(.gonderilen-mesajlar-page),
        .main-content:has(.gonderilen-mesajlar-page) {
          min-width: 0;
        }
        .page-content:has(.gonderilen-mesajlar-page) {
          overflow-x: clip;
        }
        .gonderilen-mesajlar-page > * {
          min-width: 0;
          max-width: 100%;
        }
        .gonderilen-mesajlar-page .gwm-stats-grid {
          grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
        }
        .gonderilen-mesajlar-page .gwm-stats-grid > *,
        .gonderilen-mesajlar-page .gwm-filter-grid > * {
          min-width: 0;
        }
        .gonderilen-mesajlar-page .gwm-filter-grid {
          grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr)) !important;
        }
        .gonderilen-mesajlar-page .gwm-status-tabs {
          display: flex !important;
          flex-wrap: wrap;
          max-width: 100%;
        }
        .gonderilen-mesajlar-page .gwm-table-wrapper {
          max-width: 100%;
          min-width: 0;
          overflow-x: auto;
        }
        @media (max-width: 768px) {
          .gonderilen-mesajlar-page { gap: 16px !important; }
          .gonderilen-mesajlar-page .gwm-page-header { padding: 20px !important; }
          .gonderilen-mesajlar-page .gwm-page-header h1 { font-size: 1.4rem !important; }
          .gonderilen-mesajlar-page .gwm-filter-card { padding: 16px !important; }
          .gonderilen-mesajlar-page .gwm-day-header { flex-wrap: wrap; gap: 12px; }
          .gonderilen-mesajlar-page .gwm-stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 520px) {
          .gonderilen-mesajlar-page .gwm-stats-grid { grid-template-columns: minmax(0, 1fr) !important; }
        }
      `}</style>
      {/* ─── PAGE HEADER ──────────────────────────────────────────────── */}
      <div
        className="animate-fadeInUp gwm-page-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 16,
          background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 50%, #3B82F6 100%)',
          borderRadius: 20,
          padding: '28px 32px',
          color: '#FFFFFF',
          boxShadow: '0 12px 30px rgba(37, 99, 235, 0.25)',
        }}
      >
        <div style={{ maxWidth: 650 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', padding: '6px 14px', borderRadius: 99, fontSize: '0.82rem', fontWeight: 600, marginBottom: 12 }}>
            <FaWhatsapp style={{ fontSize: '1rem', color: '#4ADE80' }} />
            <span>WhatsApp Cloud API İletim Merkezi</span>
          </div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 800, margin: 0, letterSpacing: '-0.02em', color: '#FFFFFF' }}>
            Gönderilen WhatsApp Mesajları
          </h1>
          <p style={{ margin: '8px 0 0 0', opacity: 0.9, fontSize: '0.92rem', lineHeight: 1.5 }}>
            Sistemden veli, öğrenci ve personele gönderilen tüm bildirimlerin iletim raporları, hata detayları ve gün gün geçmiş dökümü.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => fetchLogs(true)}
            disabled={refreshing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: '#FFFFFF',
              color: '#1E3A8A',
              border: 'none',
              padding: '10px 18px',
              borderRadius: 12,
              fontWeight: 700,
              fontSize: '0.88rem',
              cursor: refreshing ? 'wait' : 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              transition: 'all 0.2s',
            }}
          >
            <FiRefreshCw className={refreshing ? 'animate-spin' : ''} style={{ fontSize: '1rem' }} />
            <span>{refreshing ? 'Yenileniyor...' : 'Listeyi Yenile'}</span>
          </button>
        </div>
      </div>

      {/* ─── STATS CARDS ──────────────────────────────────────────────── */}
      <div
        className="animate-fadeInUp gwm-stats-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 16,
        }}
      >
        {/* Total card */}
        <div
          className="card"
          style={{
            padding: '20px 24px',
            borderRadius: 16,
            borderLeft: '5px solid #2563EB',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            background: 'var(--bg-card, #FFFFFF)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted, #64748B)' }}>
              Toplam Mesaj
            </span>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#EFF6FF', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiSend style={{ fontSize: '1.1rem' }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--text-primary, #0F1E3D)' }}>
              {Number(stats.total || 0).toLocaleString('tr-TR')}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted, #64748B)', marginTop: 4 }}>
              Filtrelenen: <strong>{filteredStats.total || 0}</strong>
            </div>
          </div>
        </div>

        {/* Sent / Success card */}
        <div
          className="card"
          style={{
            padding: '20px 24px',
            borderRadius: 16,
            borderLeft: '5px solid #10B981',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            background: 'var(--bg-card, #FFFFFF)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted, #64748B)' }}>
              Başarıyla İletilen
            </span>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#ECFDF5', color: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiCheckCircle style={{ fontSize: '1.1rem' }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: '1.9rem', fontWeight: 800, color: '#10B981' }}>
                {Number(stats.sent || 0).toLocaleString('tr-TR')}
              </span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669', background: '#D1FAE5', padding: '2px 8px', borderRadius: 8 }}>
                %{stats.success_rate || 0}
              </span>
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted, #64748B)', marginTop: 4 }}>
              Başarı Oranı
            </div>
          </div>
        </div>

        {/* Failed card */}
        <div
          className="card"
          style={{
            padding: '20px 24px',
            borderRadius: 16,
            borderLeft: '5px solid #EF4444',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            background: 'var(--bg-card, #FFFFFF)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted, #64748B)' }}>
              Hatalı / İletilemeyen
            </span>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#FEF2F2', color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiXCircle style={{ fontSize: '1.1rem' }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: stats.failed > 0 ? '#EF4444' : 'var(--text-primary)' }}>
              {Number(stats.failed || 0).toLocaleString('tr-TR')}
            </div>
            <div style={{ fontSize: '0.78rem', color: stats.failed > 0 ? '#DC2626' : 'var(--text-muted)', marginTop: 4, fontWeight: stats.failed > 0 ? 600 : 400 }}>
              {stats.failed > 0 ? 'Kontrol gerektiren mesajlar' : 'Hata bulunmuyor'}
            </div>
          </div>
        </div>

        {/* Pending card */}
        <div
          className="card"
          style={{
            padding: '20px 24px',
            borderRadius: 16,
            borderLeft: '5px solid #F59E0B',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            background: 'var(--bg-card, #FFFFFF)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted, #64748B)' }}>
              Kuyrukta / Bekleyen
            </span>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#FFFBEB', color: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiClock style={{ fontSize: '1.1rem' }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--text-primary, #0F1E3D)' }}>
              {Number(stats.pending || 0).toLocaleString('tr-TR')}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted, #64748B)', marginTop: 4 }}>
              Kuyrukta bekleyen bildirimler
            </div>
          </div>
        </div>

        {/* Today stats card */}
        <div
          className="card"
          style={{
            padding: '20px 24px',
            borderRadius: 16,
            borderLeft: '5px solid #8B5CF6',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            background: 'var(--bg-card, #FFFFFF)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted, #64748B)' }}>
              Bugün Gönderilen
            </span>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#F5F3FF', color: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiCalendar style={{ fontSize: '1.1rem' }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--text-primary, #0F1E3D)' }}>
              {Number(stats.today_total || 0).toLocaleString('tr-TR')}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted, #64748B)', marginTop: 4, display: 'flex', gap: 8 }}>
              <span style={{ color: '#10B981', fontWeight: 600 }}>{stats.today_sent || 0} başarılı</span>
              {stats.today_failed > 0 && (
                <span style={{ color: '#EF4444', fontWeight: 600 }}>{stats.today_failed} hatalı</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── FILTERS & SEARCH BAR ─────────────────────────────────────── */}
      <div
        className="card animate-fadeInUp gwm-filter-card"
        style={{
          padding: '20px 24px',
          borderRadius: 16,
          background: 'var(--bg-card, #FFFFFF)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Status Tabs */}
          <div className="gwm-status-tabs" style={{ display: 'inline-flex', background: 'var(--bg-elevated, #F1F5F9)', padding: 4, borderRadius: 12, gap: 4 }}>
            {[
              { id: 'all', label: 'Tümü', count: stats.total },
              { id: 'sent', label: 'Başarılı', count: stats.sent, color: '#10B981' },
              { id: 'failed', label: 'Hatalı', count: stats.failed, color: '#EF4444' },
              { id: 'pending', label: 'Bekleyen', count: stats.pending, color: '#F59E0B' },
            ].map((tab) => {
              const isActive = statusFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 9,
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: isActive ? '#FFFFFF' : 'transparent',
                    color: isActive ? (tab.color || 'var(--blue, #2563EB)') : 'var(--text-muted, #64748B)',
                    boxShadow: isActive ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span
                      style={{
                        fontSize: '0.74rem',
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: 99,
                        background: isActive ? (tab.color ? `${tab.color}15` : '#EFF6FF') : 'rgba(0,0,0,0.06)',
                        color: tab.color || 'inherit',
                      }}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Quick Date Presets */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>Tarih:</span>
            {[
              { id: 'all', label: 'Tümü' },
              { id: 'today', label: 'Bugün' },
              { id: 'yesterday', label: 'Dün' },
              { id: 'week', label: 'Son 7 Gün' },
              { id: 'month', label: 'Bu Ay' },
            ].map((p) => {
              const active = datePreset === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => handleDatePreset(p.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    border: active ? '1px solid #2563EB' : '1px solid #E2E8F0',
                    background: active ? '#EFF6FF' : '#FFFFFF',
                    color: active ? '#2563EB' : 'var(--text-secondary, #334155)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Filter inputs row */}
        <div className="gwm-filter-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {/* Search Input */}
          <div style={{ position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              className="form-input"
              placeholder="Numara, kişi adı veya hata ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: 36,
                paddingRight: searchQuery ? 32 : 12,
                height: 40,
                borderRadius: 10,
                border: '1px solid #CBD5E1',
                fontSize: '0.85rem',
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 2,
                }}
              >
                <FiX style={{ fontSize: '0.9rem' }} />
              </button>
            )}
          </div>

          {/* Type Filter */}
          <div>
            <select
              className="form-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{
                width: '100%',
                height: 40,
                borderRadius: 10,
                border: '1px solid #CBD5E1',
                fontSize: '0.85rem',
                padding: '0 12px',
                background: '#FFFFFF',
              }}
            >
              <option value="all">Tüm Bildirim Türleri</option>
              <option value="yoklama">Yoklama Bildirimi</option>
              <option value="deneme">Deneme Sonucu</option>
              <option value="turkiye_geneli_deneme">TG Deneme Sonucu</option>
              <option value="odeme">Ödeme Hatırlatması</option>
              <option value="rehberlik">Rehberlik Bildirimi</option>
              <option value="etut">Etüt Bildirimi</option>
              <option value="hesap_bilgileri">Kullanıcı Bilgileri</option>
              <option value="odev">Ödev Bildirimi</option>
              <option value="calisma_programi">Çalışma Programı</option>
            </select>
          </div>

          {/* Custom Start Date */}
          <div>
            <input
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => {
                setDatePreset('custom');
                setStartDate(e.target.value);
              }}
              style={{
                width: '100%',
                height: 40,
                borderRadius: 10,
                border: '1px solid #CBD5E1',
                fontSize: '0.85rem',
                padding: '0 12px',
              }}
              title="Başlangıç Tarihi"
            />
          </div>

          {/* Custom End Date */}
          <div>
            <input
              type="date"
              className="form-input"
              value={endDate}
              onChange={(e) => {
                setDatePreset('custom');
                setEndDate(e.target.value);
              }}
              style={{
                width: '100%',
                height: 40,
                borderRadius: 10,
                border: '1px solid #CBD5E1',
                fontSize: '0.85rem',
                padding: '0 12px',
              }}
              title="Bitiş Tarihi"
            />
          </div>
        </div>

        {/* Global Expand / Collapse Control */}
        {data.grouped_by_date?.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #F1F5F9' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Gösterilen: <strong>{data.grouped_by_date.length} gün</strong> ({data.logs?.length || 0} mesaj)
            </span>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={expandAll}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563EB',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <FiChevronDown /> Tüm Günleri Aç
              </button>
              <button
                onClick={collapseAll}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <FiChevronUp /> Tümünü Daralt
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ─── DAY BY DAY GROUPED LIST ──────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div className="spinner" style={{ margin: '0 auto 16px auto', width: 40, height: 40, borderColor: '#2563EB', borderRightColor: 'transparent' }} />
          <p style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.95rem' }}>WhatsApp mesaj kayıtları yükleniyor...</p>
        </div>
      ) : data.grouped_by_date?.length === 0 ? (
        <div
          className="card"
          style={{
            padding: '48px 24px',
            textAlign: 'center',
            borderRadius: 16,
            background: '#FFFFFF',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ width: 64, height: 64, borderRadius: 20, background: '#F1F5F9', color: '#94A3B8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
            <FaWhatsapp style={{ fontSize: '2rem' }} />
          </div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', color: 'var(--text-primary)' }}>Mesaj Kaydı Bulunamadı</h3>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.88rem', maxWidth: 450, marginInline: 'auto' }}>
            Belirtilen arama ve filtre kriterlerine uygun herhangi bir WhatsApp gönderim kaydı bulunmamaktadır.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {data.grouped_by_date.map((group) => {
            const isExpanded = expandedDays.has(group.date);
            const isToday = new Date().toISOString().slice(0, 10) === group.date;

            return (
              <div
                key={group.date}
                className="card"
                style={{
                  borderRadius: 16,
                  background: '#FFFFFF',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  border: isToday ? '1px solid #BFDBFE' : '1px solid #E2E8F0',
                }}
              >
                {/* ── Day Header ── */}
                <div
                  className="gwm-day-header"
                  onClick={() => toggleDay(group.date)}
                  style={{
                    padding: '16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    background: isToday ? '#F8FAFC' : '#FFFFFF',
                    borderBottom: isExpanded ? '1px solid #F1F5F9' : 'none',
                    transition: 'background 0.15s',
                    userSelect: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 12,
                        background: isToday ? '#EFF6FF' : '#F1F5F9',
                        color: isToday ? '#2563EB' : '#475569',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      <FiCalendar />
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text-primary, #0F1E3D)' }}>
                          {group.date_formatted}
                        </span>
                        {isToday && (
                          <span style={{ background: '#2563EB', color: '#FFFFFF', fontSize: '0.72rem', fontWeight: 800, padding: '2px 8px', borderRadius: 99 }}>
                            BUGÜN
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted, #64748B)', marginTop: 2 }}>
                        {group.stats.total} mesaj gönderildi
                      </div>
                    </div>
                  </div>

                  {/* Day Pills Summary */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span
                        style={{
                          background: '#ECFDF5',
                          color: '#059669',
                          border: '1px solid #A7F3D0',
                          padding: '3px 10px',
                          borderRadius: 8,
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <FiCheckCircle style={{ fontSize: '0.85rem' }} />
                        {group.stats.sent} Başarılı
                      </span>

                      {group.stats.failed > 0 && (
                        <span
                          style={{
                            background: '#FEF2F2',
                            color: '#DC2626',
                            border: '1px solid #FECACA',
                            padding: '3px 10px',
                            borderRadius: 8,
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <FiXCircle style={{ fontSize: '0.85rem' }} />
                          {group.stats.failed} Hatalı
                        </span>
                      )}

                      {group.stats.pending > 0 && (
                        <span
                          style={{
                            background: '#FFFBEB',
                            color: '#D97706',
                            border: '1px solid #FDE68A',
                            padding: '3px 10px',
                            borderRadius: 8,
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <FiClock style={{ fontSize: '0.85rem' }} />
                          {group.stats.pending} Bekliyor
                        </span>
                      )}
                    </div>

                    <div style={{ color: 'var(--text-muted)', fontSize: '1.2rem', display: 'flex', alignItems: 'center', marginLeft: 6 }}>
                      {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                    </div>
                  </div>
                </div>

                {/* ── Day Items Table ── */}
                {isExpanded && (
                  <div className="table-wrapper gwm-table-wrapper" style={{ overflowX: 'auto' }}>
                    <table className="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                      <thead>
                        <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#64748B', textAlign: 'left' }}>
                          <th style={{ padding: '12px 16px', width: 90 }}>Saat</th>
                          <th style={{ padding: '12px 16px', width: 140 }}>Tür / Şablon</th>
                          <th style={{ padding: '12px 16px', width: 220 }}>Alıcı</th>
                          <th style={{ padding: '12px 16px' }}>Mesaj Özeti / Parametreler</th>
                          <th style={{ padding: '12px 16px', width: 130 }}>Durum</th>
                          <th style={{ padding: '12px 16px', width: 90, textAlign: 'center' }}>İncele</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map((log) => {
                          const typeStyle = TYPE_LABELS[log.type] || {
                            label: log.type || 'WhatsApp',
                            bg: '#F1F5F9',
                            text: '#475569',
                            border: '#E2E8F0',
                          };

                          const isSent = log.status === 'sent';
                          const isFailed = log.status === 'failed';
                          const isPending = log.status === 'pending';

                          const cleanPhone = (log.normalized_to_phone || log.to_phone || log.phone || '').replace(/[^\d]/g, '');

                          return (
                            <tr
                              key={log.id}
                              style={{
                                borderBottom: '1px solid #F1F5F9',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F8FAFF'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              {/* Saat */}
                              <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                <span style={{ fontWeight: 700, color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <FiClock style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }} />
                                  {formatTime(log.created_at)}
                                </span>
                              </td>

                              {/* Tür / Şablon */}
                              <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    padding: '3px 10px',
                                    borderRadius: 8,
                                    fontSize: '0.76rem',
                                    fontWeight: 700,
                                    background: typeStyle.bg,
                                    color: typeStyle.text,
                                    border: `1px solid ${typeStyle.border}`,
                                  }}
                                >
                                  {typeStyle.label}
                                </span>
                              </td>

                              {/* Alıcı */}
                              <td style={{ padding: '12px 16px' }}>
                                <div>
                                  <div style={{ fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FiUser style={{ fontSize: '0.82rem', color: '#64748B' }} />
                                    <span>{log.alici_adi || 'Kayıtlı İsim Yok'}</span>
                                    {log.recipient_type && (
                                      <span
                                        style={{
                                          fontSize: '0.68rem',
                                          fontWeight: 700,
                                          textTransform: 'uppercase',
                                          background: '#E2E8F0',
                                          color: '#475569',
                                          padding: '1px 5px',
                                          borderRadius: 4,
                                        }}
                                      >
                                        {log.recipient_type}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FiPhone style={{ fontSize: '0.75rem' }} />
                                    <span>{formatPhoneNumber(log.to_phone || log.phone || log.normalized_to_phone)}</span>
                                    {cleanPhone && (
                                      <a
                                        href={`https://wa.me/${cleanPhone}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="WhatsApp'ta Sohbet Aç"
                                        style={{ color: '#25D366', display: 'inline-flex', alignItems: 'center' }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <FaWhatsapp />
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </td>

                              {/* Mesaj Özeti / Parametreler */}
                              <td style={{ padding: '12px 16px' }}>
                                <div
                                  style={{
                                    fontSize: '0.82rem',
                                    color: 'var(--text-secondary, #334155)',
                                    maxWidth: 450,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={log.message}
                                >
                                  {log.message || '-'}
                                </div>
                                {isFailed && (log.error_message || log.error) && (
                                  <div
                                    style={{
                                      fontSize: '0.76rem',
                                      color: '#DC2626',
                                      marginTop: 4,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      fontWeight: 600,
                                    }}
                                  >
                                    <FiAlertTriangle style={{ fontSize: '0.8rem', flexShrink: 0 }} />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {log.error_message || log.error}
                                    </span>
                                  </div>
                                )}
                              </td>

                              {/* Durum */}
                              <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                {isSent && (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      padding: '3px 8px',
                                      borderRadius: 8,
                                      fontSize: '0.76rem',
                                      fontWeight: 700,
                                      background: '#ECFDF5',
                                      color: '#059669',
                                    }}
                                  >
                                    <FiCheck style={{ fontSize: '0.85rem' }} /> İletildi
                                  </span>
                                )}
                                {isFailed && (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      padding: '3px 8px',
                                      borderRadius: 8,
                                      fontSize: '0.76rem',
                                      fontWeight: 700,
                                      background: '#FEF2F2',
                                      color: '#DC2626',
                                    }}
                                  >
                                    <FiX style={{ fontSize: '0.85rem' }} /> Başarısız
                                  </span>
                                )}
                                {isPending && (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      padding: '3px 8px',
                                      borderRadius: 8,
                                      fontSize: '0.76rem',
                                      fontWeight: 700,
                                      background: '#FFFBEB',
                                      color: '#D97706',
                                    }}
                                  >
                                    <FiClock style={{ fontSize: '0.85rem' }} /> Bekliyor
                                  </span>
                                )}
                              </td>

                              {/* Detay Butonu */}
                              <td style={{ padding: '12px 16px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                <button
                                  onClick={() => setSelectedLog(log)}
                                  className="btn"
                                  style={{
                                    padding: '5px 12px',
                                    borderRadius: 8,
                                    fontSize: '0.76rem',
                                    fontWeight: 700,
                                    background: '#F1F5F9',
                                    color: '#334155',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E2E8F0'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F1F5F9'; }}
                                >
                                  Detay
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── DETAIL MODAL ─────────────────────────────────────────────── */}
      {selectedLog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="animate-fadeInUp"
            style={{
              background: '#FFFFFF',
              borderRadius: 20,
              maxWidth: 620,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #E2E8F0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: selectedLog.status === 'sent' ? '#ECFDF5' : selectedLog.status === 'failed' ? '#FEF2F2' : '#FFFBEB',
                    color: selectedLog.status === 'sent' ? '#059669' : selectedLog.status === 'failed' ? '#DC2626' : '#D97706',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                  }}
                >
                  {selectedLog.status === 'sent' ? <FiCheckCircle /> : selectedLog.status === 'failed' ? <FiXCircle /> : <FiClock />}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    WhatsApp Mesaj Detayı #{selectedLog.id}
                  </h3>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    {new Date(selectedLog.created_at).toLocaleString('tr-TR')}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setSelectedLog(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: 4,
                  borderRadius: 8,
                }}
              >
                <FiX />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Status Alert Banner */}
              <div
                style={{
                  padding: '14px 18px',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: selectedLog.status === 'sent' ? '#F0FDF4' : selectedLog.status === 'failed' ? '#FEF2F2' : '#FFFBEB',
                  border: `1px solid ${selectedLog.status === 'sent' ? '#BBF7D0' : selectedLog.status === 'failed' ? '#FECACA' : '#FDE68A'}`,
                }}
              >
                <div style={{ fontSize: '1.3rem', color: selectedLog.status === 'sent' ? '#16A34A' : selectedLog.status === 'failed' ? '#DC2626' : '#D97706' }}>
                  {selectedLog.status === 'sent' ? <FiCheckCircle /> : selectedLog.status === 'failed' ? <FiAlertTriangle /> : <FiClock />}
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: selectedLog.status === 'sent' ? '#166534' : selectedLog.status === 'failed' ? '#991B1B' : '#92400E' }}>
                    {selectedLog.status === 'sent' ? 'Mesaj Başarıyla İletildi' : selectedLog.status === 'failed' ? 'Mesaj Gönderimi Başarısız Oldu' : 'Mesaj Kuyrukta Bekliyor'}
                  </div>
                  {selectedLog.error_message && (
                    <div style={{ fontSize: '0.8rem', color: '#B91C1C', marginTop: 2 }}>
                      Hata Detayı: {selectedLog.error_message}
                    </div>
                  )}
                </div>
              </div>

              {/* Information Grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 14,
                  background: '#F8FAFC',
                  padding: 16,
                  borderRadius: 14,
                }}
              >
                <div>
                  <span style={{ fontSize: '0.74rem', color: '#64748B', fontWeight: 600, display: 'block' }}>Alıcı Adı</span>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F1E3D' }}>{selectedLog.alici_adi || '-'}</span>
                </div>

                <div>
                  <span style={{ fontSize: '0.74rem', color: '#64748B', fontWeight: 600, display: 'block' }}>Alıcı Rolü / Tipi</span>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F1E3D', textTransform: 'capitalize' }}>{selectedLog.recipient_type || '-'}</span>
                </div>

                <div>
                  <span style={{ fontSize: '0.74rem', color: '#64748B', fontWeight: 600, display: 'block' }}>Telefon Numarası</span>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F1E3D' }}>
                    {formatPhoneNumber(selectedLog.to_phone || selectedLog.phone)}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '0.74rem', color: '#64748B', fontWeight: 600, display: 'block' }}>Bildirim Türü</span>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#2563EB' }}>
                    {TYPE_LABELS[selectedLog.type]?.label || selectedLog.type || '-'}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '0.74rem', color: '#64748B', fontWeight: 600, display: 'block' }}>Oluşturulma Saati</span>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155' }}>
                    {selectedLog.created_at ? new Date(selectedLog.created_at).toLocaleString('tr-TR') : '-'}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: '0.74rem', color: '#64748B', fontWeight: 600, display: 'block' }}>İletilme Saati</span>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155' }}>
                    {selectedLog.sent_at ? new Date(selectedLog.sent_at).toLocaleString('tr-TR') : '-'}
                  </span>
                </div>
              </div>

              {/* Message Payload / Summary */}
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: 6 }}>
                  Mesaj Metni / Şablon Parametreleri
                </label>
                <div
                  style={{
                    background: '#F1F5F9',
                    padding: '12px 16px',
                    borderRadius: 10,
                    fontSize: '0.84rem',
                    color: '#1E293B',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {selectedLog.message || '-'}
                </div>
              </div>

              {/* Provider Response JSON Viewer */}
              {selectedLog.provider_response && (
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: 6 }}>
                    Sağlayıcı (Meta WhatsApp API) Yanıtı
                  </label>
                  <pre
                    style={{
                      background: '#0F172A',
                      color: '#38BDF8',
                      padding: 14,
                      borderRadius: 10,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      overflowX: 'auto',
                      maxHeight: 160,
                      margin: 0,
                    }}
                  >
                    {typeof selectedLog.provider_response === 'string'
                      ? JSON.stringify(parseProviderResponse(selectedLog.provider_response) || selectedLog.provider_response, null, 2)
                      : JSON.stringify(selectedLog.provider_response, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '16px 24px',
                borderTop: '1px solid #E2E8F0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#F8FAFC',
                borderBottomLeftRadius: 20,
                borderBottomRightRadius: 20,
              }}
            >
              {(selectedLog.to_phone || selectedLog.phone) && (
                <a
                  href={`https://wa.me/${String(selectedLog.normalized_to_phone || selectedLog.to_phone || selectedLog.phone).replace(/[^\d]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: '#25D366',
                    color: '#FFFFFF',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    textDecoration: 'none',
                  }}
                >
                  <FaWhatsapp style={{ fontSize: '1.1rem' }} /> WhatsApp Aç
                </a>
              )}

              <button
                onClick={() => setSelectedLog(null)}
                className="btn"
                style={{
                  background: '#FFFFFF',
                  border: '1px solid #CBD5E1',
                  color: '#334155',
                  padding: '8px 20px',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  marginLeft: 'auto',
                }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
